import React, { useState, useEffect, useRef, Suspense } from 'react';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import Separator from '../../layout/Separator';
import userService from '../../../services/UserService';
import appService from '../../../services/ApplicationService';
import companyService from '../../../services/CompanyService';
import ErrorBoundary from '../../common/ErrorBoundery';
import MultipleSelect from '../../common/MultipleSelect';
import NormalTextField from '../../common/NormalTextField';
import HRService from '../../../services/HrService';
import RequestService from '../../../services/RequestService';
import { useNavigate } from 'react-router-dom';
import { validate as isValidUUID } from 'uuid';
import LockPersonRoundedIcon from '@mui/icons-material/LockPersonRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import logo from '../../../assets/images/logo.jpeg';
import LogoLoader from '../../common/LogoLoader';
import Cookies from 'js-cookie';

const RequestForm = () => {
    // State management
    const [users, setUsers] = useState([]);
    const [apps, setApps] = useState([]);
    const [rolesSelection, setRolesSelection] = useState([]);
    const [emailSelection, setEmailSelection] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [requestor, setRequestor] = useState([]);
    const [approver, setApprover] = useState([]);
    const [requesttype, setRequestType] = useState([]);
    const [approverList, setApproverList] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [error, setError] = useState(null);
    const [accessRequestData, setAccessRequestData] = useState({});
    const [accessApproverData, setAccessApproverData] = useState([]);
    const [systemSelection, setSystemSelection] = useState([]);
    const [selectedSystem, setSelectedSystem] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingRoles, setIsLoadingRoles] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormComplete, setIsFormComplete] = useState(false);
    const [formErrors, setFormErrors] = useState({});
    const [activeStep, setActiveStep] = useState(0);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [fieldsTouched, setFieldsTouched] = useState({
        requestType: false,
        system: false,
        roles: false,
        requestor: false,
        approver: false,
        comments: false
    });

    const saveTimeout = useRef(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const navigate = useNavigate();

    // Check for verified email when component loads
    useEffect(() => {
        const verifiedEmail = sessionStorage.getItem('verified_email');
        if (!verifiedEmail) {
            console.warn('No verified email found in session storage');
            setSnackbarMessage('Authentication required. Redirecting to login page...');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            
            // Allow time for the snackbar to be seen before redirecting
            setTimeout(() => {
                navigate('/myrequests');
            }, 2500);
        }
    }, [navigate]);

    // Check form completion status
    useEffect(() => {
        const requiredFields = {
            requestType: !!requesttype?.value,
            system: !!selectedSystem?.value,
            roles: selectedRoles.length > 0,
            requestor: requestor.length > 0 || requesttype?.value === 'Pending Approval',
            approver: approver.length > 0 || requesttype?.value === 'Pre-Approval',
            comments: !!accessRequestData.COMMENTS && accessRequestData.COMMENTS.trim().length >= 10
        };
        
        // Only set errors for fields that have been touched
        setFormErrors({
            requestType: fieldsTouched.requestType && !requiredFields.requestType ? 'Request type is required' : '',
            system: fieldsTouched.system && !requiredFields.system ? 'System selection is required' : '',
            roles: fieldsTouched.roles && !requiredFields.roles ? 'At least one role must be selected' : '',
            requestor: fieldsTouched.requestor && !requiredFields.requestor ? 'At least one requestor must be selected' : '',
            approver: fieldsTouched.approver && !requiredFields.approver && requesttype?.value === 'Pending Approval' ? 'At least one approver must be selected' : '',
            comments: fieldsTouched.comments && !requiredFields.comments ? 'Business rationale is required (minimum 10 characters)' : ''
        });
        
        setIsFormComplete(
            requiredFields.requestType && 
            requiredFields.system && 
            requiredFields.roles && 
            requiredFields.requestor && 
            (requiredFields.approver || requesttype?.value === 'Pre-Approval') &&
            requiredFields.comments
        );
    }, [requesttype, selectedSystem, selectedRoles, requestor, approver, accessRequestData.COMMENTS, fieldsTouched]);

    // Mark fields as touched when they're interacted with
    const markFieldTouched = (fieldName) => {
        setFieldsTouched(prev => ({
            ...prev,
            [fieldName]: true
        }));
    };

    useEffect(() => {
        const fetchAppList = async () => {
            try {
                setIsLoading(true);
                // Record start time for minimum loading time
                const loadingStartTime = Date.now();
                const minLoadingTime = 2000; // 2 seconds minimum
                
                // Ensure we're using the authenticated axiosInstance
                const token = localStorage.getItem('token') || Cookies.get('token');
                if (!token) {
                    console.warn('No authentication token found when fetching apps');
                }
                
                const appList = await appService.fetchApps();

                if (appList) {
                    const apps = appList.map(item => ({
                        value: item.id,
                        label: `${item.APP_NAME} (${item.COMPANY_NAME})`
                    }));

                    setSystemSelection(apps);
                }
                
                // Calculate how much time to wait to meet minimum loading time
                const elapsedTime = Date.now() - loadingStartTime;
                const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
                
                // Ensure we show loading state for at least minLoadingTime
                if (remainingTime > 0) {
                    await new Promise(resolve => setTimeout(resolve, remainingTime));
                }
                
            } catch (error) {
                console.error(`Error fetching application data: ${error.message}`);
                if (error.response) {
                    console.error('Response status:', error.response.status);
                    console.error('Response data:', error.response.data);
                }
                setSnackbarMessage('Failed to load applications. Please try refreshing the page.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            } finally {
                setIsLoading(false);
            }
        };

        const fetchHRList = async () => {
            try {
                setIsLoading(true);
                // Record start time for minimum loading time
                const loadingStartTime = Date.now();
                const minLoadingTime = 2000; // 2 seconds minimum
                
                // Ensure we're using the authenticated axiosInstance
                const token = localStorage.getItem('token') || Cookies.get('token');
                if (!token) {
                    console.warn('No authentication token found when fetching employees');
                }
                
                console.log("Fetching active employees data...");
                const hrList = await HRService.fetchActiveEmployees();

                console.log("Active employees response:", hrList);
                
                // Log the format of the first employee to understand structure
                if (hrList && hrList.length > 0) {
                    console.log("Sample employee data format:", hrList[0]);
                    console.log("Email format in employee data:", hrList[0].value);
                    console.log("Label format in employee data:", hrList[0].label);
                }

                if (!hrList) {
                    console.error("Employee list is null or undefined");
                    setSnackbarMessage('No employee data received from server');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }

                if (!Array.isArray(hrList)) {
                    console.error("Employee list is not an array:", hrList);
                    setSnackbarMessage('Invalid employee data format received');
                    setSnackbarSeverity('error');
                    setSnackbarOpen(true);
                    return;
                }

                if (hrList.length === 0) {
                    console.warn("Employee list is empty");
                    setSnackbarMessage('No active employees found in the system');
                    setSnackbarSeverity('warning');
                    setSnackbarOpen(true);
                    return;
                }

                // The API is already returning objects with value and label properties
                // We can use them directly instead of transforming the data
                setEmailSelection(hrList);
                console.log(`Loaded ${hrList.length} active employees`);
                
                // Calculate how much time to wait to meet minimum loading time
                const elapsedTime = Date.now() - loadingStartTime;
                const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
                
                // Ensure we show loading state for at least minLoadingTime
                if (remainingTime > 0) {
                    await new Promise(resolve => setTimeout(resolve, remainingTime));
                }

            } catch (error) {
                console.error(`Error fetching active employees:`, error);
                
                // More detailed error reporting
                if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    console.error("Error response:", {
                        data: error.response.data,
                        status: error.response.status,
                        headers: error.response.headers
                    });
                    setSnackbarMessage(`Failed to load employee data: Server error (${error.response.status})`);
                } else if (error.request) {
                    // The request was made but no response was received
                    console.error("No response received:", error.request);
                    setSnackbarMessage('Failed to load employee data: No response from server');
                } else {
                    // Something happened in setting up the request that triggered an Error
                    console.error("Request error:", error.message);
                    setSnackbarMessage(`Failed to load employee data: ${error.message}`);
                }
                
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAppList();
        fetchHRList();
    }, []);

    const handleRoleChange = (selectedOptions) => {
        markFieldTouched('roles');
        const selectedRoles = selectedOptions.map(option => option.value);
        const rolesString = selectedRoles.join(',');
        setSelectedRoles(selectedOptions);

        const updatedData = {
            ...accessRequestData,
            ROLES: rolesString
        };

        setAccessRequestData(updatedData);
    };

    const handleRequestor = (selectedOptions) => {
        markFieldTouched('requestor');
        const requestorValues = selectedOptions.map(option => option.value);
        const requestorString = requestorValues.join(',');
        setRequestor(selectedOptions);

        const updatedData = {
            ...accessRequestData,
            REQUESTOR: requestorString
        };

        setAccessRequestData(updatedData);
        setApproverList([]);
        setApprover([]);

        // Filter the emailSelection to remove any items whose value is in the requestorValues array
        // Also filter out the creator email for segregation of duties
        // Also filter out inactive employees
        const creatorEmail = sessionStorage.getItem('verified_email');
        const approverList = emailSelection.filter(item => 
            !requestorValues.includes(item.value) && 
            item.value !== creatorEmail &&
            item.status !== 'INACTIVE'
        );
        setApproverList(approverList);
    };

    const handleApprover = (selectedOptions) => {
        markFieldTouched('approver');
        const approver = selectedOptions.map(option => option.value);
        const approverString = approver.join(',');
        setApprover(selectedOptions);

        // Update both the approval data and the main request data
        const updatedApprovalData = {
            ...accessApproverData,
            APPROVER: approver
        };

        // Simply use the email address directly
        // This matches how it's done for Users/Requestors
        const updatedRequestData = {
            ...accessRequestData,
            ASSIGNED_TO: approverString
        };

        console.log('Approver values:', approver);
        console.log('ASSIGNED_TO set to:', approverString);

        setAccessApproverData(updatedApprovalData);
        setAccessRequestData(updatedRequestData);
    };

    const handleRequestType = (selectedOptions) => {
        markFieldTouched('requestType');
        const requesttype = selectedOptions.value;
        setRequestType(selectedOptions);

        const updatedData = {
            ...accessRequestData,
            REQUEST_TYPE: requesttype
        };

        setAccessRequestData(updatedData);
        
        // Clear requestor and approver when request type changes
        setRequestor([]);
        setApprover([]);
        
        // For 'Requires Approval', we'll set appropriate validation in the UI later
        // For 'Pre-Approval', we'll set appropriate validation in the UI later
    };

    const handleComments = (e) => {
        const { name, value } = e.target;
        markFieldTouched('comments');

        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
        }
        
        const updatedData = {
            ...accessRequestData,
            [name]: value
        };

        setAccessRequestData(updatedData);
    };

    const handleCommentsBlur = (e) => {
        const { name, value } = e.target;
        markFieldTouched('comments');

        const updatedData = {
            ...accessRequestData,
            [name]: value
        };

        setAccessRequestData(updatedData);
    };

    const handleSystemSelectionChange = async (selectedOptions) => {
        markFieldTouched('system');
        const selectedSystem = selectedOptions.value;
        setSelectedSystem(selectedOptions);

        const updatedData = {
            ...accessRequestData,
            APP_NAME: selectedSystem,
        };
        
        setAccessRequestData(updatedData);

        try {
            // Use the dedicated loading state for roles
            setIsLoadingRoles(true);
            
            // Clear roles first to avoid showing stale data
            setRolesSelection([]);
            setSelectedRoles([]);
            
            // Add a deliberate delay for better UX (2 seconds)
            const roles = await Promise.all([
                appService.fetchAppsRecordById(selectedSystem),
                new Promise(resolve => setTimeout(resolve, 2000)) // 2 second delay
            ]).then(([rolesData]) => rolesData);
            
            if (roles && Array.isArray(roles)) {
                const roleNames = [...new Set(roles.map(item => item.ROLE_NAME))];

                const formattedRoleName = roleNames.map(item => ({
                    value: item,
                    label: item,
                }));

                setRolesSelection(formattedRoleName);
            }
        } catch (error) {
            console.error("Error fetching roles:", error);
            setSnackbarMessage('Failed to load roles for the selected system');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsLoadingRoles(false);
        }
    };

    const submitRequest = async (e) => {
        e.preventDefault();
        
        // Mark all fields as touched when submitting
        setFieldsTouched({
            requestType: true,
            system: true,
            roles: true,
            requestor: true,
            approver: true,
            comments: true
        });
        
        if (!isFormComplete) {
            setSnackbarMessage('Please complete all required fields');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        // Get the verified email (CREATOR)
        const creatorEmail = sessionStorage.getItem('verified_email');
        
        // Validation: For all request types, validate segregation of duties
        const requestorValues = requestor.map(option => option.value);
        const approverValues = approver.map(option => option.value);
        
        // Check if the creator is in the approver list (not allowed)
        if (approverValues.includes(creatorEmail)) {
            setSnackbarMessage('The creator cannot be an approver for segregation of duties');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        // For Pre-Approval, CREATOR should not be in REQUESTOR 
        if (requesttype?.value === 'Pre-Approval' && requestorValues.includes(creatorEmail)) {
            setSnackbarMessage('For Pre-Approved requests, you cannot add yourself as a requestor');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        // Validate that REQUESTOR is not also an APPROVER
        const hasOverlap = requestorValues.some(value => approverValues.includes(value));
        if (hasOverlap) {
            setSnackbarMessage('The same user cannot be both a requestor and an approver');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        // Validate that business rationale is provided
        if (!accessRequestData.COMMENTS || accessRequestData.COMMENTS.trim().length < 10) {
            setSnackbarMessage('Please provide a clear business rationale for this access request');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        // Open confirmation dialog instead of submitting immediately
        setConfirmDialogOpen(true);
    };
    
    const handleConfirmSubmit = async () => {
        // Close the confirmation dialog
        setConfirmDialogOpen(false);
        
        try {
            setIsSubmitting(true);
            
            // Ensure we show loading state for at least 2 seconds before submission
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            const response = await RequestService.fetchRequestID();
            const now = new Date().toISOString();
            
            // Get the verified email from sessionStorage
            const creatorEmail = sessionStorage.getItem('verified_email');
            
            if (!creatorEmail) {
                console.warn('No verified email found in session storage');
            }

            // If request type is "Pending Approval", make REQUESTOR = CREATOR
            // Otherwise for "Pre-Approval", CREATOR = ASSIGNED_TO
            let requestorString = '';
            let assignedToString = '';
            
            if (requesttype.value === 'Pending Approval') {
                // For "Requires Approval", REQUESTOR = CREATOR
                requestorString = creatorEmail;
                
                // Get approver values
                const approverValues = approver.map(option => option.value);
                assignedToString = approverValues.join(',');
            } else if (requesttype.value === 'Pre-Approval') {
                // For "Pre-Approval", ASSIGNED_TO = CREATOR
                // and REQUESTOR is what the user selected
                const requestorValues = requestor.map(option => option.value);
                requestorString = requestorValues.join(',');
                assignedToString = creatorEmail;
            }

            if (response) {
                if (requesttype.value === 'Pending Approval') {
                    const updatedData = {
                        ...accessRequestData,
                        REQUEST_ID: 'REQ_ID#' + response.request_id_counter,
                        DATE_REQUESTED: now,
                        STATUS: 'Pending Approval',
                        LAST_MODIFIED: now,
                        CREATOR: creatorEmail || '',
                        REQUESTOR: requestorString,
                        ASSIGNED_TO: assignedToString
                    };

                    console.log('Submitting request with data:', updatedData);
                    const request = await RequestService.createRequest(updatedData);

                    // Create approval records for each approver
                    for (const approverObj of approver) {
                        const approverData = {
                            REQUEST_ID: request?.id,
                            APPROVER: [approverObj.value], // Send as a list
                            APPROVED_BY_EMAIL: approverObj.value, // Only send email, let backend resolve UUID
                            APPROVER_LABEL: approverObj.label, // Add label for backend lookup
                            DATE_REQUESTED: now,
                            STATUS: 'Pending Approval',
                            LAST_MODIFIED: now,
                            HANDLE_UUID_IN_BACKEND: true // Flag for backend to handle UUID resolution
                        };

                        console.log('Creating approval with data:', approverData);
                        const approval = await RequestService.createApproval(approverData);

                        if (approval) {
                            console.log('Sending email approval notification');
                            // Send email notification with the same data
                            await RequestService.sendEmailApproval(approverData);
                        }
                    }
                  
                    navigate(`/request/success/${request.id}`);
                } else if (requesttype.value === 'Pre-Approval') {
                    const updatedData = {
                        ...accessRequestData,
                        REQUEST_ID: 'REQ_ID#' + response.request_id_counter,
                        DATE_REQUESTED: now,
                        STATUS: 'Approved',
                        LAST_MODIFIED: now,
                        CREATOR: creatorEmail || '',
                        REQUESTOR: requestorString,
                        ASSIGNED_TO: assignedToString
                    };

                    console.log('Submitting pre-approved request with data:', updatedData);
                    const request = await RequestService.createRequest(updatedData);
                    
                    try {
                        // Let the backend handle UUID resolution by providing the email
                        // This is more secure and reliable than handling UUID lookup in frontend
                        const approverData = {
                            REQUEST_ID: request?.id,
                            APPROVER: [creatorEmail],
                            APPROVED_BY: creatorEmail, // Backend will resolve this to a UUID
                            APPROVED_BY_EMAIL: creatorEmail, // Adding explicit email field for backend to use
                            APPROVER_LABEL: 'System Administrator', // Add label for backend lookup
                            DATE_REQUESTED: now,
                            STATUS: 'Approved',
                            LAST_MODIFIED: now,
                            DATE_APPROVED: now,
                            HANDLE_UUID_IN_BACKEND: true // Flag for backend to handle UUID resolution
                        };

                        console.log('Creating approval with data:', approverData);
                        const approval = await RequestService.createApproval(approverData);

                        if (approval) {
                            console.log('Sending email approval notification');
                            await RequestService.sendEmailApproval(approverData);
                        }

                        navigate(`/request/success/${request.id}`);
                    } catch (error) {
                        console.error('Error processing pre-approved request:', error);
                        setSnackbarMessage('Unable to process request. Please try again or contact support.');
                        setSnackbarSeverity('error');
                        setSnackbarOpen(true);
                    }
                }
            } else {
                console.error('No request ID returned from API');
                setSnackbarMessage('Failed to generate request ID');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error('Error generating request ID:', error);
            setSnackbarMessage('Failed to submit request');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleCancelSubmit = () => {
        setConfirmDialogOpen(false);
    };

    const requestTypeSelection = [
        { value: 'Pre-Approval', label: 'Pre-Approved Request' },
        { value: 'Pending Approval', label: 'Requires Approval' },
    ];

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const steps = [
        { label: 'Request Type', completed: !!requesttype?.value },
        { label: 'System & Roles', completed: !!selectedSystem?.value && selectedRoles.length > 0 },
        { label: 'Users & Approvers', completed: requestor.length > 0 && (approver.length > 0 || requesttype?.value === 'Pre-Approval') },
    ];

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <mui.Box sx={{ py: 2 }}>
                        <mui.Typography variant="body2" color="text.secondary" mb={3}>
                            Select the type of access request
                        </mui.Typography>
                        
                        <MultipleSelect
                            isMultiSelect={false}
                            placeholderText="Select Request Type"
                            selectedOptions={requesttype}
                            selectOptions={requestTypeSelection}
                            value={requesttype}
                            handleChange={handleRequestType}
                            error={!!formErrors.requestType}
                        />
                        
                        {formErrors.requestType && (
                            <mui.Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                {formErrors.requestType}
                            </mui.Typography>
                        )}
                    </mui.Box>
                );
            case 1:
                return (
                    <mui.Box sx={{ py: 2 }}>
                        <mui.Typography variant="body2" color="text.secondary" mb={3}>
                            Select the system and roles
                        </mui.Typography>
                        
                        <mui.Box sx={{ mb: 3 }}>
                            <mui.Typography variant="subtitle2" color="text.primary" mb={1}>
                                System
                            </mui.Typography>
                            <MultipleSelect
                                isMultiSelect={false}
                                placeholderText="Select System"
                                selectedOptions={selectedSystem}
                                selectOptions={systemSelection}
                                value={selectedSystem}
                                handleChange={handleSystemSelectionChange}
                                error={!!formErrors.system}
                            />
                            
                            {formErrors.system && (
                                <mui.Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                    {formErrors.system}
                                </mui.Typography>
                            )}
                        </mui.Box>
                        
                        <mui.Box>
                            <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                <mui.Typography variant="subtitle2" color="text.primary">
                                    Roles
                                </mui.Typography>
                                
                                {isLoadingRoles && (
                                    <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <mui.Box sx={{ position: 'relative', display: 'inline-flex', mr: 1 }}>
                                            <mui.CircularProgress 
                                                size={16} 
                                                thickness={6}
                                                sx={{ 
                                                    color: '#1976d2',
                                                    animation: 'fadeIn 0.3s ease-in'
                                                }}
                                            />
                                        </mui.Box>
                                        <mui.Typography 
                                            variant="caption" 
                                            color="primary"
                                            sx={{ 
                                                fontWeight: 500,
                                                animation: 'fadeIn 0.3s ease-in',
                                                '@keyframes fadeIn': {
                                                    '0%': { opacity: 0 },
                                                    '100%': { opacity: 1 }
                                                }
                                            }}
                                        >
                                            Loading roles...
                                        </mui.Typography>
                                    </mui.Box>
                                )}
                            </mui.Box>
                            
                            {isLoadingRoles ? (
                                <mui.Box 
                                    sx={{ 
                                        height: '56px', 
                                        border: '1px solid', 
                                        borderColor: 'divider',
                                        borderRadius: '4px',
                                        bgcolor: alpha('#f8f9fa', 0.8),
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}
                                >
                                    <mui.Box 
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            px: 2,
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <mui.Skeleton 
                                                variant="rectangular" 
                                                width={120} 
                                                height={20} 
                                                animation="wave"
                                                sx={{ borderRadius: '4px' }}
                                            />
                                        </mui.Box>
                                        
                                        <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {[0, 1, 2].map((index) => (
                                                <mui.Box 
                                                    key={index}
                                                    sx={{ 
                                                        width: 6, 
                                                        height: 6, 
                                                        borderRadius: '50%',
                                                        mx: 0.5,
                                                        bgcolor: '#1976d2',
                                                        opacity: 0.6,
                                                        animation: `pulse 1.5s ease-in-out ${index * 0.2}s infinite`
                                                    }}
                                                />
                                            ))}
                                        </mui.Box>
                                    </mui.Box>
                                    
                                    {/* Pulse animation for the loading bar */}
                                    <mui.Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: 0,
                                            left: 0,
                                            height: '2px',
                                            bgcolor: '#1976d2',
                                            animation: 'loading 2s ease-in-out infinite',
                                            '@keyframes loading': {
                                                '0%': { width: '0%', left: '0%', right: '100%' },
                                                '50%': { width: '70%', left: '30%', right: '0%' },
                                                '100%': { width: '0%', left: '100%', right: '0%' }
                                            },
                                            '@keyframes pulse': {
                                                '0%': { opacity: 0.4, transform: 'scale(0.8)' },
                                                '50%': { opacity: 1, transform: 'scale(1.2)' },
                                                '100%': { opacity: 0.4, transform: 'scale(0.8)' }
                                            }
                                        }}
                                    />
                                </mui.Box>
                            ) : (
                                <MultipleSelect
                                    isMultiSelect={true}
                                    placeholderText="Select Roles"
                                    selectedOptions={selectedRoles}
                                    selectOptions={rolesSelection}
                                    value={selectedRoles}
                                    handleChange={handleRoleChange}
                                    error={!!formErrors.roles}
                                />
                            )}
                            
                            {formErrors.roles && !isLoadingRoles && (
                                <mui.Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                    {formErrors.roles}
                                </mui.Typography>
                            )}
                        </mui.Box>
                    </mui.Box>
                );
            case 2:
                return (
                    <mui.Box sx={{ py: 2 }}>
                        <mui.Typography variant="body2" color="text.secondary" mb={3}>
                            Select users and approvers
                        </mui.Typography>
                        
                        {/* Show requestor field only for Pre-Approved Request */}
                        {requesttype?.value === 'Pre-Approval' && (
                        <mui.Box sx={{ mb: 3 }}>
                            <mui.Typography variant="subtitle2" color="text.primary" mb={1}>
                                Users
                            </mui.Typography>
                            <MultipleSelect
                                isMultiSelect={true}
                                placeholderText="Select Users"
                                selectedOptions={requestor}
                                selectOptions={emailSelection.filter(item => 
                                    item.value !== sessionStorage.getItem('verified_email') &&
                                    item.status !== 'INACTIVE'
                                ).map(item => ({
                                    value: item.value,
                                    label: item.label,
                                    email: item.value
                                }))}
                                value={requestor}
                                handleChange={handleRequestor}
                                error={!!formErrors.requestor}
                            />
                            
                            {formErrors.requestor && (
                                <mui.Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                    {formErrors.requestor}
                                </mui.Typography>
                            )}
                        </mui.Box>
                        )}
                        
                        {/* If Requires Approval, show only approvers field */}
                        {requesttype?.value === 'Pending Approval' && (
                        <mui.Box sx={{ mb: 3 }}>
                                <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <InfoOutlinedIcon sx={{ fontSize: '1rem', mr: 1, color: 'primary.main' }} />
                                    <mui.Typography variant="body2" color="text.secondary">
                                        For "Requires Approval" requests, you are automatically set as the requestor.
                                    </mui.Typography>
                                </mui.Box>
                            <mui.Typography variant="subtitle2" color="text.primary" mb={1}>
                                Approvers
                            </mui.Typography>
                            <MultipleSelect
                                isMultiSelect={true}
                                placeholderText="Select Approvers"
                                selectedOptions={approver}
                                selectOptions={emailSelection.filter(item => 
                                    item.value !== sessionStorage.getItem('verified_email') && 
                                    item.status !== 'INACTIVE'
                                ).map(item => ({
                                    value: item.value,
                                    label: item.label,
                                    // Include the email directly in the object for use in the dialog
                                    email: item.value
                                }))}
                                value={approver}
                                handleChange={handleApprover}
                                error={!!formErrors.approver}
                                customStyles={{
                                    option: (provided) => ({
                                        ...provided,
                                        padding: '8px 12px',
                                        '& div': {
                                            marginBottom: '4px'
                                        },
                                        '& small': {
                                            color: '#666',
                                            fontSize: '0.75rem',
                                            display: 'block',
                                            marginTop: '2px'
                                        }
                                    })
                                }}
                            />
                            
                            {formErrors.approver && (
                                <mui.Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                    {formErrors.approver}
                                </mui.Typography>
                            )}
                        </mui.Box>
                        )}
                        
                        {/* Show approvers field for Pre-Approval with note */}
                        {requesttype?.value === 'Pre-Approval' && (
                            <mui.Box sx={{ mb: 3 }}>
                                <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <InfoOutlinedIcon sx={{ fontSize: '1rem', mr: 1, color: 'primary.main' }} />
                                    <mui.Typography variant="body2" color="text.secondary">
                                        For "Pre-Approved" requests, you are automatically set as the approver.
                                    </mui.Typography>
                                </mui.Box>
                            </mui.Box>
                        )}
                        
                        <mui.Box>
                            <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <mui.Typography variant="subtitle2" color="text.primary" mb={1}>
                                    Business Rationale
                            </mui.Typography>
                                <mui.Typography variant="caption" color="error" sx={{ mb: 1 }}>*</mui.Typography>
                            </mui.Box>
                            <mui.Box sx={{ mb: 1 }}>
                                <mui.Typography variant="caption" color="text.secondary">
                                    Explain why this access is needed for business purposes. Minimum 10 characters required.
                                </mui.Typography>
                            </mui.Box>
                            <NormalTextField
                                name="COMMENTS"
                                value={accessRequestData.COMMENTS || ''}
                                onChange={handleComments}
                                onBlur={handleCommentsBlur}
                                isMultiLine={true}
                                rows={3}
                                placeholder="Provide business justification for this access request..."
                                error={!!formErrors.comments}
                                helperText={formErrors.comments || ''}
                            />
                        </mui.Box>
                    </mui.Box>
                );
            default:
                return null;
        }
    };

    const handleNext = () => {
        // Mark fields in the current step as touched
        if (activeStep === 0) {
            markFieldTouched('requestType');
        } else if (activeStep === 1) {
            markFieldTouched('system');
            markFieldTouched('roles');
        } else if (activeStep === 2) {
            markFieldTouched('requestor');
            markFieldTouched('approver');
        }

        // Validate current step before proceeding
        const isStepValid = isCurrentStepComplete();
        
        if (isStepValid) {
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        } else {
            // Show error message if step is not complete
            setSnackbarMessage('Please complete all required fields in this step');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const isCurrentStepComplete = () => {
        return steps[activeStep].completed;
    };

    const customMainContent = (
        <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f7' }}>
            <ResponsiveContainer>
                {/* Simplified header with logo similar to sidebar */}
                <mui.Box 
                    sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        bgcolor: '#0D6EFD', 
                        color: 'white',
                        p: 1,
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        mb: 2,
                        mt: 3
                    }}
                >
                    <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img src={logo} width="35" height="35" alt="Audit-AI Logo" style={{ marginRight: '10px' }} />
                        <mui.Typography variant="h6" sx={{ fontSize: '25px' }}>
                            Audit-AI
                        </mui.Typography>
                    </mui.Box>
                </mui.Box>
                <Separator />

                {/* Back navigation */}
                <mui.Box sx={{ mt: 2, mb: 1 }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link 
                            underline="hover" 
                            color="inherit" 
                            onClick={() => navigate('/myrequests')}
                            sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                cursor: 'pointer',
                                '&:hover': { color: 'primary.main' }
                            }}
                        >
                            <mui.Icon sx={{ mr: 0.5, fontSize: '20px' }}>arrow_back</mui.Icon>
                            Back to My Requests
                        </mui.Link>
                    </mui.Breadcrumbs>
                </mui.Box>

                {isLoading && (
                    <mui.Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        position: 'fixed', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        backgroundColor: alpha('#fff', 0.8), 
                        zIndex: 1300,
                        backdropFilter: 'blur(4px)' 
                    }}>
                        <LogoLoader size={60} message="Loading form data..." />
                    </mui.Box>
                )}
                
                {isSubmitting && (
                    <mui.Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        position: 'fixed', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        backgroundColor: alpha('#fff', 0.9), 
                        zIndex: 1400,
                        backdropFilter: 'blur(8px)' 
                    }}>
                        <mui.Box sx={{ textAlign: 'center' }}>
                            <LogoLoader size={70} message="Processing your access request. Please wait a moment..." />
                        </mui.Box>
                        
                        <mui.Box 
                            sx={{ 
                                mt: 4, 
                                width: 200, 
                                height: 4, 
                                borderRadius: 2,
                                bgcolor: alpha('#1976d2', 0.1),
                                overflow: 'hidden'
                            }}
                        >
                            <mui.Box 
                                sx={{ 
                                    height: '100%', 
                                    bgcolor: '#1976d2',
                                    animation: 'progress 2s ease-in-out infinite',
                                    '@keyframes progress': {
                                        '0%': { width: '0%' },
                                        '50%': { width: '70%' },
                                        '100%': { width: '100%' }
                                    }
                                }} 
                            />
                        </mui.Box>
                    </mui.Box>
                )}
                
                {/* Confirmation Dialog */}
                <mui.Dialog
                    open={confirmDialogOpen}
                    onClose={handleCancelSubmit}
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
                            maxWidth: '520px',
                            width: '100%',
                            animation: 'dialogFadeIn 0.3s ease-out',
                            '@keyframes dialogFadeIn': {
                                '0%': { opacity: 0, transform: 'translateY(20px)' },
                                '100%': { opacity: 1, transform: 'translateY(0)' }
                            }
                        }
                    }}
                >
                    <mui.DialogTitle
                        sx={{
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            px: 3,
                            py: 2.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5
                        }}
                    >
                        <mui.Box
                            sx={{
                                bgcolor: alpha('#1976d2', 0.1),
                                borderRadius: '50%',
                                width: 36,
                                height: 36,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <LockPersonRoundedIcon color="primary" fontSize="small" />
                        </mui.Box>
                        <mui.Typography variant="h6" fontWeight={600}>
                            Confirm Access Request
                        </mui.Typography>
                    </mui.DialogTitle>
                    
                    <mui.DialogContent sx={{ p: 0 }}>
                        <mui.Box sx={{ px: 3, pt: 3, pb: 2 }}>
                            <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.5 }}>
                                Please review the details of your access request before submitting:
                            </mui.Typography>
                            
                            <mui.Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    mb: 2,
                                    bgcolor: alpha('#f8f9fa', 0.6),
                                    border: '1px solid',
                                    borderColor: alpha('#000', 0.06),
                                    borderRadius: '10px'
                                }}
                            >
                                <mui.Box 
                                    sx={{ 
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2.5,
                                    }}
                                >
                                    <mui.Box>
                                        <mui.Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
                                            Request Type
                                        </mui.Typography>
                                        <mui.Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                bgcolor: alpha('#1976d2', 0.08),
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: '6px'
                                            }}
                                        >
                                            <mui.Typography variant="body2" fontWeight={600} color="primary.main">
                                                {requesttype?.label || 'Not specified'}
                                            </mui.Typography>
                                        </mui.Box>
                                    </mui.Box>
                                    
                                    <mui.Box>
                                        <mui.Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
                                            System
                                        </mui.Typography>
                                        <mui.Typography variant="body2" fontWeight={600}>
                                            {selectedSystem?.label || 'Not specified'}
                                        </mui.Typography>
                                    </mui.Box>
                                    
                                    <mui.Box>
                                        <mui.Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
                                            Roles
                                        </mui.Typography>
                                        <mui.Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.5 }}>
                                            {selectedRoles.length > 0 ? (
                                                selectedRoles.map((role, index) => (
                                                    <mui.Chip
                                                        key={index}
                                                        label={role.label}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: alpha('#1976d2', 0.08),
                                                            color: 'primary.dark',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500,
                                                            height: '26px',
                                                            borderRadius: '6px'
                                                        }}
                                                    />
                                                ))
                                            ) : (
                                                <mui.Typography variant="body2" fontWeight={500}>
                                                    No roles selected
                                                </mui.Typography>
                                            )}
                                        </mui.Box>
                                    </mui.Box>
                                    
                                    <mui.Divider sx={{ opacity: 0.6 }} />
                                    
                                    <mui.Box>
                                        <mui.Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
                                            Users
                                        </mui.Typography>
                                        <mui.Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7, mt: 0.5 }}>
                                            {requesttype?.value === 'Pending Approval' ? (
                                                // For "Requires Approval", show the current user
                                                <mui.Box 
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: 1
                                                    }}
                                                >
                                                    <mui.Avatar 
                                                        sx={{ 
                                                            width: 24, 
                                                            height: 24,
                                                            fontSize: '0.8rem',
                                                            bgcolor: 'primary.light',
                                                            color: 'primary.dark',
                                                            mt: 0.5
                                                        }}
                                                    >
                                                        {sessionStorage.getItem('verified_email')?.substring(0, 2) || 'U'}
                                                    </mui.Avatar>
                                                    <mui.Box>
                                                        <mui.Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.2 }}>
                                                            Current User (You)
                                                        </mui.Typography>
                                                        <mui.Typography 
                                                            variant="caption" 
                                                            color="text.secondary"
                                                            sx={{ 
                                                                display: 'block',
                                                                fontSize: '0.75rem',
                                                                mt: 0.5
                                                            }}
                                                        >
                                                            {sessionStorage.getItem('verified_email')}
                                                        </mui.Typography>
                                                    </mui.Box>
                                                </mui.Box>
                                            ) : requestor.length > 0 ? (
                                                // For "Pre-Approval", show selected users
                                                requestor.map((user, index) => (
                                                    <mui.Box 
                                                        key={index}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            gap: 1
                                                        }}
                                                    >
                                                        <mui.Avatar 
                                                            sx={{ 
                                                                width: 24, 
                                                                height: 24,
                                                                fontSize: '0.8rem',
                                                                bgcolor: `hsl(${(index * 70) % 360}, 60%, 85%)`,
                                                                color: `hsl(${(index * 70) % 360}, 70%, 30%)`,
                                                                mt: 0.5
                                                            }}
                                                        >
                                                            {user.label.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                                        </mui.Avatar>
                                                        <mui.Box>
                                                            <mui.Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.2 }}>
                                                                {user.label}
                                                            </mui.Typography>
                                                            <mui.Typography 
                                                                variant="caption" 
                                                                color="text.secondary"
                                                                sx={{ 
                                                                    display: 'block',
                                                                    fontSize: '0.75rem',
                                                                    mt: 0.5
                                                                }}
                                                            >
                                                                {user.email || user.value}
                                                            </mui.Typography>
                                                        </mui.Box>
                                                    </mui.Box>
                                                ))
                                            ) : (
                                                <mui.Typography variant="body2" fontWeight={500}>
                                                    No users selected
                                                </mui.Typography>
                                            )}
                                        </mui.Box>
                                    </mui.Box>
                                    
                                    {requesttype?.value === 'Pending Approval' && (
                                        <mui.Box>
                                            <mui.Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
                                                Approvers
                                            </mui.Typography>
                                            <mui.Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7, mt: 0.5 }}>
                                                {approver.length > 0 ? (
                                                    approver.map((user, index) => (
                                                        <mui.Box 
                                                            key={index}
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'flex-start',
                                                                gap: 1
                                                            }}
                                                        >
                                                            <mui.Avatar 
                                                                sx={{ 
                                                                    width: 24, 
                                                                    height: 24,
                                                                    fontSize: '0.8rem',
                                                                    bgcolor: `hsl(${(index * 40 + 120) % 360}, 60%, 85%)`,
                                                                    color: `hsl(${(index * 40 + 120) % 360}, 70%, 30%)`,
                                                                    mt: 0.5
                                                                }}
                                                            >
                                                                {user.label.split(' ').map(n => n[0]).join('').substring(0, 2)}
                                                            </mui.Avatar>
                                                            <mui.Box>
                                                                <mui.Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.2 }}>
                                                                    {user.label}
                                                                </mui.Typography>
                                                                <mui.Typography 
                                                                    variant="caption" 
                                                                    color="text.secondary"
                                                                    sx={{ 
                                                                        display: 'block',
                                                                        fontSize: '0.75rem',
                                                                        mt: 0.5
                                                                    }}
                                                                >
                                                                    {user.email || user.value}
                                                                </mui.Typography>
                                                            </mui.Box>
                                                        </mui.Box>
                                                    ))
                                                ) : (
                                                    <mui.Typography variant="body2" fontWeight={500}>
                                                        No approvers selected
                                                    </mui.Typography>
                                                )}
                                            </mui.Box>
                                        </mui.Box>
                                    )}

                                    {requesttype?.value === 'Pre-Approval' && (
                                        <mui.Box>
                                            <mui.Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
                                                Approver (You)
                                            </mui.Typography>
                                            <mui.Box 
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mt: 0.5
                                                }}
                                            >
                                                <mui.Avatar 
                                                    sx={{ 
                                                        width: 24, 
                                                        height: 24,
                                                        fontSize: '0.8rem',
                                                        bgcolor: 'success.light',
                                                        color: 'success.dark'
                                                    }}
                                                >
                                                    {sessionStorage.getItem('verified_email')?.substring(0, 2) || 'U'}
                                                </mui.Avatar>
                                                <mui.Typography variant="body2" fontWeight={500}>
                                                    {sessionStorage.getItem('verified_email') || 'Current User'} (You)
                                                </mui.Typography>
                                            </mui.Box>
                                        </mui.Box>
                                    )}
                                    
                                    {accessRequestData.COMMENTS && (
                                        <mui.Box>
                                            <mui.Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, display: 'block' }}>
                                                Comments
                                            </mui.Typography>
                                            <mui.Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    mt: 0.5,
                                                    p: 1.5,
                                                    bgcolor: alpha('#f8f9fa', 0.8),
                                                    border: '1px solid',
                                                    borderColor: alpha('#000', 0.06),
                                                    borderRadius: '6px',
                                                    fontStyle: 'italic',
                                                    color: 'text.secondary'
                                                }}
                                            >
                                                "{accessRequestData.COMMENTS}"
                                            </mui.Typography>
                                        </mui.Box>
                                    )}
                                </mui.Box>
                            </mui.Paper>
                        </mui.Box>
                    </mui.DialogContent>
                    
                    <mui.DialogActions
                        sx={{
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            px: 3,
                            py: 2.5,
                            gap: 1.5
                        }}
                    >
                        <mui.Button
                            onClick={handleCancelSubmit}
                            variant="outlined"
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px',
                                fontWeight: 600,
                                px: 2
                            }}
                        >
                            Cancel
                        </mui.Button>
                        <mui.Button
                            onClick={handleConfirmSubmit}
                            variant="contained"
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px',
                                fontWeight: 600,
                                boxShadow: 'none',
                                px: 3,
                                '&:hover': {
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                                }
                            }}
                            endIcon={<mui.Icon>send</mui.Icon>}
                        >
                            {isSubmitting ? (
                                <>
                                    <mui.Box
                                        sx={{
                                            position: 'absolute',
                                            left: '50%',
                                            transform: 'translateX(-50%)'
                                        }}
                                    >
                                        <LogoLoader size={24} />
                                    </mui.Box>
                                    <span style={{ visibility: 'hidden' }}>Submit Request</span>
                                </>
                            ) : 'Review & Submit'}
                        </mui.Button>
                    </mui.DialogActions>
                </mui.Dialog>

                <mui.Snackbar
                    open={snackbarOpen}
                    autoHideDuration={6000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <mui.Alert 
                        onClose={handleSnackbarClose} 
                        severity={snackbarSeverity} 
                        elevation={0}
                        variant="filled"
                        sx={{ 
                            borderRadius: '8px',
                            fontWeight: 500
                        }}
                    >
                        {snackbarMessage}
                    </mui.Alert>
                </mui.Snackbar>

                <mui.Box 
                    sx={{ 
                        maxWidth: '850px',
                        margin: '20px auto',
                        padding: { xs: '0 16px', md: 0 }
                    }}
                >
                    <mui.Paper 
                        elevation={0} 
                        sx={{ 
                            borderRadius: '16px',
                            overflow: 'hidden',
                            border: '1px solid',
                            borderColor: 'divider',
                            background: '#ffffff',
                            mb: 4
                        }}
                    >
                        <mui.Box 
                            sx={{ 
                                bgcolor: 'primary.main',
                                color: 'white',
                                py: 2.5,
                                px: 4,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <LockPersonRoundedIcon fontSize="medium" />
                            <mui.Box>
                                <mui.Typography variant="h6" fontWeight={500}>
                                    Access Request Form
                                </mui.Typography>
                                <mui.Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                    Complete all steps to submit your request
                                </mui.Typography>
                            </mui.Box>
                        </mui.Box>
                        
                        <mui.Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 3 } }}>
                            <mui.Stepper 
                                activeStep={activeStep} 
                                alternativeLabel
                                sx={{ 
                                    mb: 4,
                                    '& .MuiStepLabel-root': {
                                        color: 'text.secondary'
                                    },
                                    '& .MuiStepIcon-root': {
                                        color: 'grey.400',
                                        '&.Mui-active': {
                                            color: 'primary.main',
                                        },
                                        '&.Mui-completed': {
                                            color: 'success.main',
                                        }
                                    }
                                }}
                            >
                                {steps.map((step, index) => (
                                    <mui.Step key={step.label} completed={step.completed}>
                                        <mui.StepLabel>{step.label}</mui.StepLabel>
                                    </mui.Step>
                                ))}
                            </mui.Stepper>
                            
                            {getStepContent(activeStep)}
                            
                            <mui.Divider sx={{ my: 3 }} />
                            
                            <mui.Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <mui.Button
                                    variant="outlined"
                                    onClick={handleBack}
                                    disabled={activeStep === 0}
                                    sx={{ 
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        px: 3
                                    }}
                                >
                                    Back
                                </mui.Button>
                                
                                {activeStep === steps.length - 1 ? (
                                    <mui.Button
                                        variant="contained"
                                        onClick={submitRequest}
                                        disabled={!isFormComplete || isSubmitting}
                                        sx={{ 
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            px: 3,
                                            py: 1,
                                            boxShadow: 'none',
                                            minWidth: '140px',
                                            position: 'relative',
                                            '&:hover': {
                                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                                            }
                                        }}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <mui.Box
                                                    sx={{
                                                        position: 'absolute',
                                                        left: '50%',
                                                        transform: 'translateX(-50%)'
                                                    }}
                                                >
                                                    <LogoLoader size={24} />
                                                </mui.Box>
                                                <span style={{ visibility: 'hidden' }}>Submit Request</span>
                                            </>
                                        ) : 'Review & Submit'}
                                    </mui.Button>
                                ) : (
                                    <mui.Button
                                        variant="contained"
                                        onClick={handleNext}
                                        disabled={!isCurrentStepComplete()}
                                        sx={{ 
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            px: 3,
                                            py: 1,
                                            boxShadow: 'none',
                                            '&:hover': {
                                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                                            }
                                        }}
                                    >
                                        Next
                                    </mui.Button>
                                )}
                            </mui.Box>
                        </mui.Box>
                    </mui.Paper>
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    return (
        <ErrorBoundary fallback={<p>Failed to load data.</p>} error={error}>
            <Suspense fallback={
                <mui.Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    height: '100vh',
                    flexDirection: 'column',
                    gap: 2
                }}>
                    <LogoLoader size={60} message="Loading access request form..." />
                </mui.Box>
            }>
                {customMainContent}
            </Suspense>
        </ErrorBoundary>
    );
};

export default RequestForm;
